import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import api from '../utils/api';

const COLORS = ['#00ff88', '#00e5ff', '#ff6600', '#ff0099', '#9900ff', '#ffee00'];

export default function Portfolio() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get('/portfolio').then((r) => setData(r.data)).catch(() => {});
  }, []);

  const holdings = data?.holdings || [];
  const summary = data?.summary || { total_qty: 0, total_value: 0, total_cost: 0, total_pnl: 0, total_pnl_pct: '0.00' };
  const tokenizedHoldings = data?.tokenized_holdings || [];
  const custodyCash = data?.custody_cash;

  const allocationData = useMemo(() => holdings.map((item, index) => ({
    ...item,
    fill: COLORS[index % COLORS.length],
  })), [holdings]);

  if (!data) return <div className="loading"><div className="spinner" /></div>;

  const topMetrics = [
    { label: 'Total holdings', value: `${summary.total_qty.toLocaleString('en-IN')} tCO2e`, copy: 'Credits currently available across inventory', color: 'var(--neon-green)' },
    { label: 'Portfolio value', value: `Rs.${summary.total_value.toLocaleString('en-IN')}`, copy: 'Current marked value using reference pricing', color: 'var(--neon-cyan)' },
    { label: 'Total P&L', value: `${summary.total_pnl >= 0 ? '+' : ''}Rs.${summary.total_pnl.toLocaleString('en-IN')}`, copy: `Change ${summary.total_pnl_pct}%`, color: summary.total_pnl >= 0 ? 'var(--neon-green)' : 'var(--neon-pink)' },
    { label: 'Offset equivalent', value: `${summary.total_qty.toLocaleString('en-IN')} T`, copy: `About ${Math.round(summary.total_qty / 21)} cars per year`, color: 'var(--neon-orange)' },
  ];

  return (
    <div className="page-shell page-stack">
      <section className="surface-panel glow-surface" style={{ padding: '1.8rem' }}>
        <div className="section-header-row">
          <div className="section-stack">
            <div className="section-tag">Portfolio</div>
            <h1 className="section-title">Holdings redesigned to feel cleaner, calmer, and easier to act on</h1>
            <p className="section-copy">
              This page now brings value, allocation, and P&L into a more balanced operating view instead of a dense reporting screen.
            </p>
          </div>
          <div className="inline-actions">
            <Link to="/trade" className="btn btn-primary">Open trade desk</Link>
            <Link to="/transactions" className="btn btn-secondary">Transactions</Link>
          </div>
        </div>
      </section>

      {custodyCash && (
        <section className="surface-panel" style={{ padding: '1.4rem' }}>
          <div className="section-tag">Custody wallet</div>
          <div className="section-grid-2" style={{ marginTop: '1rem' }}>
            <div className="metric-tile">
              <div className="eyebrow">Available INR</div>
              <div className="value" style={{ color: 'var(--neon-green)' }}>Rs.{Number(custodyCash.available_inr || 0).toLocaleString('en-IN')}</div>
            </div>
            <div className="metric-tile">
              <div className="eyebrow">Reserved INR</div>
              <div className="value" style={{ color: 'var(--neon-cyan)' }}>Rs.{Number(custodyCash.reserved_inr || 0).toLocaleString('en-IN')}</div>
            </div>
          </div>
        </section>
      )}

      <section className="metric-strip">
        {topMetrics.map((item) => (
          <div key={item.label} className="metric-tile">
            <div className="eyebrow">{item.label}</div>
            <div className="value" style={{ color: item.color }}>{item.value}</div>
            <div className="copy-sm" style={{ marginTop: '0.35rem' }}>{item.copy}</div>
          </div>
        ))}
      </section>

      <section className="section-grid-2">
        <div className="surface-panel" style={{ padding: '1.4rem' }}>
          <div className="section-header-row">
            <div className="section-stack" style={{ gap: '0.35rem' }}>
              <div className="section-tag">Holdings</div>
              <div className="headline-md">Current inventory by credit type</div>
            </div>
            {holdings.length > 0 && <span className="badge badge-green">{holdings.length} active rows</span>}
          </div>

          {holdings.length === 0 ? (
            <div className="surface-muted" style={{ padding: '1.25rem', marginTop: '1rem' }}>
              <div className="copy-sm">No holdings yet. Browse the marketplace and complete your first buy to populate the portfolio.</div>
            </div>
          ) : (
            <table className="soft-table" style={{ marginTop: '1rem' }}>
              <thead>
                <tr>
                  <th>Credit type</th>
                  <th>Holdings</th>
                  <th>Avg cost</th>
                  <th>Value</th>
                  <th>P&L</th>
                </tr>
              </thead>
              <tbody>
                {holdings.map((item, index) => (
                  <tr key={`${item.credit_type}-${index}`}>
                    <td style={{ color: COLORS[index % COLORS.length], fontWeight: 700 }}>{item.credit_type}</td>
                    <td>{item.qty.toLocaleString('en-IN')} tCO2e</td>
                    <td>Rs.{item.avg_cost.toLocaleString('en-IN')}</td>
                    <td>Rs.{item.current_value.toLocaleString('en-IN')}</td>
                    <td style={{ color: item.pnl >= 0 ? 'var(--neon-green)' : 'var(--neon-pink)' }}>
                      {item.pnl >= 0 ? '+' : ''}Rs.{item.pnl.toLocaleString('en-IN')} ({item.pnl_pct}%)
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="panel-stack">
          <div className="surface-panel" style={{ padding: '1.4rem' }}>
            <div className="section-stack" style={{ gap: '0.35rem' }}>
              <div className="section-tag">Allocation</div>
              <div className="headline-sm">Portfolio mix by quantity</div>
            </div>

            {holdings.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={allocationData} dataKey="qty" nameKey="credit_type" cx="50%" cy="50%" innerRadius={56} outerRadius={86}>
                      {allocationData.map((item, index) => <Cell key={`${item.credit_type}-${index}`} fill={item.fill} />)}
                    </Pie>
                    <Tooltip contentStyle={{ background: '#0d0f1a', border: '1px solid rgba(255,255,255,0.1)', fontFamily: 'Manrope, sans-serif', borderRadius: '16px' }} />
                  </PieChart>
                </ResponsiveContainer>

                <div className="panel-stack" style={{ gap: '0.55rem' }}>
                  {allocationData.map((item) => (
                    <div key={item.credit_type} className="summary-row">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
                        <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: item.fill, flexShrink: 0 }} />
                        <span className="copy-sm" style={{ color: 'var(--text)' }}>{item.credit_type.split('(')[0].trim()}</span>
                      </div>
                      <span className="value" style={{ color: item.fill }}>{((item.qty / summary.total_qty) * 100).toFixed(1)}%</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="surface-muted" style={{ padding: '1.25rem', marginTop: '1rem' }}>
                <div className="copy-sm">Allocation will appear after your first executed holding.</div>
              </div>
            )}
          </div>

          {data.market_source?.label && (
            <div className="surface-panel" style={{ padding: '1.2rem' }}>
              <div className="eyebrow">Valuation source</div>
              <div className="copy-sm" style={{ marginTop: '0.45rem' }}>{data.market_source.label}</div>
            </div>
          )}
        </div>
      </section>

      {tokenizedHoldings.length > 0 && (
        <section className="surface-panel" style={{ padding: '1.4rem' }}>
          <div className="section-tag">Tokenized holdings</div>
          <table className="soft-table" style={{ marginTop: '1rem' }}>
            <thead>
              <tr>
                <th>Token</th>
                <th>Project</th>
                <th>Available</th>
                <th>Reserved</th>
                <th>Retired</th>
              </tr>
            </thead>
            <tbody>
              {tokenizedHoldings.map((item) => (
                <tr key={`${item.symbol}-${item.vintage}`}>
                  <td>{item.symbol}</td>
                  <td>{item.project_name}</td>
                  <td>{Number(item.available_qty).toLocaleString('en-IN')}</td>
                  <td>{Number(item.reserved_qty).toLocaleString('en-IN')}</td>
                  <td>{Number(item.retired_qty).toLocaleString('en-IN')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}
